/* Created by Interactive 63 - https://interactive63.com */
/**
 * Applying presets and offsetting animation. Both are draft mutators: they change a project
 * inside a store update so the whole operation is one undo step.
 */
import { createId } from '../model/ids';
import {
  CONTAINER_TAGS,
  type Easing,
  type ElementId,
  type Keyframe,
  type MotionHandles,
  type Project,
  type PropertyKey,
} from '../model/types';
import { canAddProperty, ensureTrack, moveKeyframes } from '../timeline/keyframes';
import { staggerOrder, type StaggerItem, type StaggerOrder } from './stagger';
import { presetReason } from './helpers';
import type { PresetContext, PresetDefinition, PresetParams } from './types';

export const MAX_SCENE_DURATION_MS = 120000;
/** The transform origin an element keeps until someone moves it. */
const DEFAULT_ORIGIN = { x: 50, y: 50 };
const REASON_PROPERTY_BLOCKED =
  "This layer can't take the properties this preset animates (for example it already has a shape morph).";
const REASON_EMPTY_GROUP = 'This group is empty';
const REASON_GROUP_PREFIX = 'Nothing inside this group fits: ';

export interface ApplyOptions {
  /** When the first element starts, in ms. */
  start: number;
  /** Length of the preset (the cycle length for loop presets). */
  duration: number;
  order: StaggerOrder;
  seed?: number;
  /** Delay between successive elements, in ms. */
  staggerMs: number;
  extendScene: boolean;
  /** Replaces the easing of every keyframe except the last. */
  easing?: Easing;
  /** Sets `restartWithScene: false` on the tracks created (entrances). */
  playOnce?: boolean;
  /** Describes an element at a time. The editor reads the rendered artwork for this. */
  contextFor: (elementId: ElementId, time: number) => PresetContext;
  /** Screen centres for position-based stagger orders. */
  centres?: Record<ElementId, { x: number; y: number }>;
  /**
   * Elements to animate in place of an element, for example the shapes inside an icon group when
   * the preset only suits shapes. The group keeps its place in the stagger order and all of its
   * shapes start together.
   */
  expand?: Record<ElementId, readonly ElementId[]>;
}

/** An element a preset was not applied to, with the reason in plain language. */
export interface SkippedElement {
  id: ElementId;
  reason: string;
}

export interface ApplyResult {
  applied: ElementId[];
  skipped: SkippedElement[];
  /** Keyframes dropped because they would pass the scene end and the scene was not extended. */
  dropped: number;
  /** The new scene duration when it was extended. */
  extendedTo?: number;
}

interface Placement {
  elementId: ElementId;
  property: PropertyKey;
  keyframes: {
    time: number;
    value: Keyframe['value'];
    easing: Easing;
    motion?: MotionHandles;
  }[];
}

/**
 * Why a preset could not be applied to an element. A group has no outline or fill of its own, so
 * its reason comes from the first shape inside it.
 */
export function skipReason(
  draft: Project,
  preset: PresetDefinition,
  elementId: ElementId,
  contextFor: ApplyOptions['contextFor'],
  start: number,
): string {
  const own = presetReason(preset, contextFor(elementId, start));
  const meta = draft.elements[elementId];
  if (!meta || !CONTAINER_TAGS.includes(meta.tag)) return own ?? REASON_PROPERTY_BLOCKED;
  const queue = [...meta.childIds];
  while (queue.length > 0) {
    const childId = queue.shift() as ElementId;
    const child = draft.elements[childId];
    if (!child) continue;
    if (CONTAINER_TAGS.includes(child.tag)) {
      queue.unshift(...child.childIds);
      continue;
    }
    const reason = presetReason(preset, contextFor(childId, start));
    if (reason) return REASON_GROUP_PREFIX + reason;
  }
  return meta.childIds.length === 0 ? REASON_EMPTY_GROUP : (own ?? REASON_PROPERTY_BLOCKED);
}

/** One line naming each reason and how many layers it applies to, for a toast or hint. */
export function skipSummary(skipped: readonly SkippedElement[]): string {
  const counts = new Map<string, number>();
  for (const { reason } of skipped) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  return [...counts]
    .map(([reason, count]) => `${count} ${count === 1 ? 'layer' : 'layers'}: ${reason}`)
    .join('. ');
}

/** Orders the elements for stagger from their layer order and screen centres. */
function orderedIds(
  elementIds: readonly ElementId[],
  options: Pick<ApplyOptions, 'order' | 'seed' | 'centres'>,
): ElementId[] {
  const items: StaggerItem[] = elementIds.map((id, index) => ({
    id,
    index,
    centre: options.centres?.[id] ?? { x: 0, y: 0 },
  }));
  return staggerOrder(items, options.order, options.seed ?? 1);
}

/**
 * Applies a preset to elements. Each element starts `staggerMs` after the one before it, in the
 * chosen order. Existing keyframes of a touched property inside the preset's time range are
 * replaced, and keyframes outside it are kept, so an entrance and an exit can stack. Loop presets
 * replace the whole track and repeat across the scene instead.
 */
export function applyPreset(
  draft: Project,
  elementIds: readonly ElementId[],
  preset: PresetDefinition,
  params: PresetParams,
  options: ApplyOptions,
): ApplyResult {
  const isLoop = preset.category === 'loop';
  const result: ApplyResult = { applied: [], skipped: [], dropped: 0 };
  const placements: Placement[] = [];

  orderedIds(elementIds, options).forEach((unitId, rank) => {
    const start = (isLoop ? 0 : Math.max(0, Math.round(options.start))) + rank * options.staggerMs;
    let placed = false;
    for (const elementId of options.expand?.[unitId] ?? [unitId]) {
      const context = options.contextFor(elementId, start);
      if (preset.appliesTo(context) !== true) continue;
      for (const track of preset.build(context, params)) {
        if (!canAddProperty(draft, elementId, track.property)) continue;
        const last = track.keyframes.length - 1;
        placements.push({
          elementId,
          property: track.property,
          keyframes: track.keyframes.map((k, i) => ({
            time: start + Math.round(k.offset * options.duration),
            value: k.value,
            easing: options.easing && i < last ? options.easing : k.easing,
            ...(k.motion ? { motion: k.motion } : {}),
          })),
        });
        placed = true;
        const meta = draft.elements[elementId];
        const atDefaultOrigin =
          meta && meta.origin.x === DEFAULT_ORIGIN.x && meta.origin.y === DEFAULT_ORIGIN.y;
        if (preset.pivot && atDefaultOrigin) meta.origin = { ...preset.pivot };
      }
    }
    if (placed) result.applied.push(unitId);
    else {
      result.skipped.push({
        id: unitId,
        reason: skipReason(draft, preset, unitId, options.contextFor, start),
      });
    }
  });

  const latest = Math.max(0, ...placements.flatMap((p) => p.keyframes.map((k) => k.time)));
  if (latest > draft.scene.duration && options.extendScene) {
    draft.scene.duration = Math.min(MAX_SCENE_DURATION_MS, latest);
    result.extendedTo = draft.scene.duration;
  }

  for (const placement of placements) {
    const keyframes = placement.keyframes.filter((k) => {
      const fits = k.time <= draft.scene.duration;
      if (!fits) result.dropped += 1;
      return fits;
    });
    if (keyframes.length === 0) continue;
    const track = ensureTrack(draft, placement.elementId, placement.property);
    const first = Math.min(...keyframes.map((k) => k.time));
    const lastTime = Math.max(...keyframes.map((k) => k.time));
    const kept = isLoop
      ? []
      : track.keyframes.filter((kf) => kf.time < first || kf.time > lastTime);
    const created: Keyframe[] = [];
    for (const k of keyframes) {
      const existing = created.find((c) => c.time === k.time);
      if (existing) {
        existing.value = k.value;
        existing.easing = k.easing;
        if (k.motion) existing.motion = k.motion;
      } else {
        created.push({
          id: createId('kf'),
          time: k.time,
          value: k.value,
          easing: k.easing,
          ...(k.motion ? { motion: k.motion } : {}),
        });
      }
    }
    track.keyframes = [...kept, ...created].sort((a, b) => a.time - b.time);
    track.enabled = true;
    if (isLoop) track.repeat = { period: null, count: 'infinite', alternate: false };
    if (options.playOnce) track.restartWithScene = false;
  }
  return result;
}

export interface OffsetOptions {
  offsetMs: number;
  order: StaggerOrder;
  seed?: number;
  extendScene: boolean;
  centres?: Record<ElementId, { x: number; y: number }>;
}

/**
 * Shifts each element's keyframes (all tracks) later by its rank times the offset, so identical
 * animations cascade. Returns the new scene duration if it had to be extended.
 */
export function offsetAnimation(
  draft: Project,
  elementIds: readonly ElementId[],
  options: OffsetOptions,
): { shifted: ElementId[]; extendedTo?: number } {
  const ordered = orderedIds(elementIds, options);
  const idsFor = (elementId: ElementId): string[] =>
    draft.tracks
      .filter((t) => t.elementId === elementId)
      .flatMap((t) => t.keyframes.map((k) => k.id));
  const endOf = (elementId: ElementId): number =>
    Math.max(
      0,
      ...draft.tracks
        .filter((t) => t.elementId === elementId)
        .flatMap((t) => t.keyframes.map((k) => k.time)),
    );

  let extendedTo: number | undefined;
  if (options.extendScene) {
    const needed = Math.max(
      0,
      ...ordered.map((id, rank) => endOf(id) + rank * Math.max(0, options.offsetMs)),
    );
    if (needed > draft.scene.duration) {
      draft.scene.duration = Math.min(MAX_SCENE_DURATION_MS, needed);
      extendedTo = draft.scene.duration;
    }
  }
  const shifted: ElementId[] = [];
  ordered.forEach((id, rank) => {
    const ids = idsFor(id);
    if (ids.length === 0 || rank === 0) return;
    moveKeyframes(draft, ids, rank * options.offsetMs);
    shifted.push(id);
  });
  return { shifted, extendedTo };
}
